'use client';

import {
  Badge,
  HookDataTable as DataTable,
  HookDataTableColumnHeader as DataTableColumnHeader,
  type HookDataTableFilterField,
  HookDataTableToolbar as DataTableToolbar,
} from '@mcdougald/ui/src';
import {
  type ColumnDef,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { isArray } from 'lodash';
import { CalendarClock, CircleIcon } from 'lucide-react';

import {
  ALL_UNIQUE_CATEGORIES,
  FULL_TESTIMONY_LIST,
} from '@/constants/data/testimonials';
import { type Testimonial } from '@/types';

const categoryOptions = ALL_UNIQUE_CATEGORIES.map((category) => {
  const matchingTestimonies = FULL_TESTIMONY_LIST.filter((t) =>
    t.categories?.includes(category),
  );

  return {
    value: category,
    label: `${category} (${matchingTestimonies.length})`,
    icon: CircleIcon,
  };
});

const yearOptions = ['2025', '2024', '2023', '2022', '2021', '2020'].map(
  (year) => {
    const matchingTestimonies = FULL_TESTIMONY_LIST.filter(
      (t) => t.year === Number(year) || String(t.year) === year,
    );

    return {
      value: year,
      label:
        matchingTestimonies.length === 0
          ? year
          : `${year} (${matchingTestimonies.length})`,
      icon: CalendarClock,
    };
  },
);

const columns2: Array<ColumnDef<Testimonial>> = [
  // {
  //   id: 'select',
  //   header: ({ table }) => (
  //     <Checkbox
  //       checked={
  //         table.getIsAllPageRowsSelected() ||
  //         (table.getIsSomePageRowsSelected() && 'indeterminate')
  //       }
  //       onCheckedChange={(value) => {
  //         table.toggleAllPageRowsSelected(!!value);
  //       }}
  //       aria-label="Select all"
  //       className="translate-y-[2px]"
  //     />
  //   ),
  //   cell: ({ row }) => (
  //     <Checkbox
  //       checked={row.getIsSelected()}
  //       onCheckedChange={(value) => {
  //         row.toggleSelected(!!value);
  //       }}
  //       aria-label="Select row"
  //       className="translate-y-[2px]"
  //     />
  //   ),
  //   enableSorting: false,
  //   enableHiding: false,
  // },
  {
    accessorKey: 'relation',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Source" />
    ),
    cell: ({ row }) => (
      <div className="w-[80px]">{row.getValue('relation')}</div>
    ),
    enableSorting: true,
    enableHiding: false,
  },
  {
    accessorKey: 'testimonial',
    size: 400,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Testimony" />
    ),
    cell: ({ row }) => (
      <div className="flex">{row.getValue('testimonial')}</div>
    ),
    enableSorting: true,
    enableHiding: false,
  },
  {
    accessorKey: 'categories',
    size: 130,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Catagories" />
    ),
    cell: ({ row }) => {
      const value: string[] | undefined = row.getValue('categories');
      const isEmpty = value?.length === 0 || !isArray(value);
      return (
        <div className="w-[80px] flex flex-col gap-1">
          {isEmpty
            ? null
            : value.map((c) => {
                return (
                  <Badge
                    key={`${row.id}-${c}`}
                    variant={'ghost'}
                    className={'w-fit !text-xs'}
                  >
                    {c}
                  </Badge>
                );
              })}
        </div>
      );
    },
    enableSorting: true,
    enableHiding: false,
  },
  {
    accessorKey: 'year',
    size: 100,
    maxSize: 100,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Date" />
    ),
    cell: ({ row }) => (
      <div className="w-[80px] text-center">{row.getValue('year')}</div>
    ),
    enableSorting: true,
    enableHiding: false,
  },
];

const filterFields2: Array<HookDataTableFilterField<Testimonial>> = [
  // {
  //   id: 'relation',
  //   label: 'Source',
  //   placeholder: 'Filter source...',
  // },
  {
    id: 'testimonial',
    label: 'Testimony',
    placeholder: 'Filter references...',
  },
  {
    id: 'categories',
    label: 'Tags',
    options: categoryOptions,
  },
  {
    id: 'year',
    label: 'Date',
    options: yearOptions,
  },
];

const TestimoniesDataTable = () => {
  const table = useReactTable<Testimonial>({
    data: FULL_TESTIMONY_LIST,
    columns: columns2,
    enableRowSelection: true,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    initialState: {
      pagination: {
        pageIndex: 1, //custom initial page index
        pageSize: 5, //custom default page size
      },
    },
  });

  return (
    <DataTable table={table}>
      <DataTableToolbar table={table} filterFields={filterFields2} />
    </DataTable>
  );
};

export default TestimoniesDataTable;
